import { DisplayMode, Platform } from './platform.model'

import { Section, SectionComponent } from '../constants/sections.constants'

// Re-export the enums for backward compatibility
export { Section, SectionComponent }

/** Chat section paths that vary by display mode (Fill, FullScreen, Floating) */
export const CHAT_DISPLAY_MODE_PATHS = [
  Section.Chat,
  SectionComponent.ChatHeader,
  SectionComponent.ChatHeaderButton,
  SectionComponent.ChatHistory,
  SectionComponent.ChatSuggestedPrompts,
  SectionComponent.ChatSuggestedPromptsSection,
  SectionComponent.ChatSuggestedPromptsItem,
  SectionComponent.ChatHistorySection,
  SectionComponent.ChatHistoryItem,
  SectionComponent.ChatHistorySearch,
  SectionComponent.ChatMessages,
  SectionComponent.ChatAgentMessage,
  SectionComponent.ChatUserMessage,
  SectionComponent.ChatMessageActions,
  SectionComponent.ChatMessageFeedback,
  SectionComponent.ChatMessageFeedbackReason,
  SectionComponent.ChatUserVoiceMessage,
  SectionComponent.ChatVoiceInput,
  SectionComponent.ChatInput,
  SectionComponent.ChatInputArea,
  SectionComponent.ChatErrorMessage,
  SectionComponent.ChatUserFileAttachments,
  SectionComponent.ChatUserFileItem,
  SectionComponent.ChatInputFileAttachments,
  SectionComponent.ChatInputFileItem,
] as const

const DISPLAY_MODE_SUFFIX: Record<DisplayMode, string> = {
  [DisplayMode.FILL]: '',
  [DisplayMode.FULLSCREEN]: 'FullScreen',
  [DisplayMode.FLOATING]: 'Floating',
}

// SECTIONS

export const ThemePathsMap: Record<Section | SectionComponent, string> = {
  [Section.Color]: 'tokens.colors',
  [Section.Typography]: 'tokens.typography',
  [Section.Shadows]: 'tokens.shadows',
  [Section.GeneralSettings]: 'general',
  [Section.InputSelection]: 'groups.inputSelection',
  [Section.TextLink]: 'groups.textLink',
  [Section.RadioCheckbox]: 'groups.radioCheckbox',
  [Section.Utilities]: 'utilities',
  [Section.Button]: 'components.button',
  [Section.DocumentPreview]: 'components.documentPreview',
  [Section.Layouts]: 'groups.layouts',
  [Section.Switch]: 'components.switch',
  [Section.Slider]: 'components.slider',
  [Section.Message]: 'components.message',
  [Section.Stepper]: 'components.stepper',
  [Section.TabBar]: 'components.tabBar',
  [Section.NavigationBar]: 'components.navigation',
  [Section.SegmentedButton]: 'components.segmentedButton',
  [Section.Table]: 'components.table',
  [Section.Chat]: 'components.chat',
  [Section.BulkFileUpload]: 'components.bulkFileUpload',
  [Section.Breadcrumbs]: 'components.breadcrumbs',
  [Section.Separator]: 'components.separator',
  [Section.ProgressBar]: 'components.progressBar',
  [Section.Tooltip]: 'components.tooltip',
  [Section.Markdown]: 'components.markdown',
  [Section.Modal]: 'components.modal',
  [SectionComponent.SelectOption]: 'components.selectOption',
  [SectionComponent.MultiSelectOption]: 'components.multiSelectOption',
  [SectionComponent.MultiSelect]: 'components.multiSelect',
  [SectionComponent.MultiSelectChips]: 'components.chips',
  [SectionComponent.DatepickerCalendar]: 'components.datePickerCalendar',
  [SectionComponent.Select]: 'components.select',
  [SectionComponent.Datepicker]: 'components.datePicker',
  [SectionComponent.Label]: 'components.label',
  [SectionComponent.Error]: 'components.error',
  [SectionComponent.Helper]: 'components.helper',
  [SectionComponent.HelperTooltip]: 'components.helperTooltip',
  [SectionComponent.InputSelectionLabel]: 'components.inputSelectionLabel',
  [SectionComponent.InputSelectionError]: 'components.inputSelectionError',
  [SectionComponent.InputSelectionHelper]: 'components.inputSelectionHelper',
  [SectionComponent.RadioCheckboxLabel]: 'components.radioCheckboxLabel',
  [SectionComponent.RadioCheckboxError]: 'components.radioCheckboxError',
  [SectionComponent.RadioCheckboxHelper]: 'components.radioCheckboxHelper',
  [SectionComponent.SliderLabel]: 'components.sliderLabel',
  [SectionComponent.SliderError]: 'components.sliderError',
  [SectionComponent.SliderHelper]: 'components.sliderHelper',
  [SectionComponent.SegmentedButtonLabel]: 'components.segmentedButtonLabel',
  [SectionComponent.SegmentedButtonError]: 'components.segmentedButtonError',
  [SectionComponent.SegmentedButtonHelper]: 'components.segmentedButtonHelper',
  [SectionComponent.Text]: 'components.text',
  [SectionComponent.Link]: 'components.link',
  [SectionComponent.Radio]: 'components.radio',
  [SectionComponent.Checkbox]: 'components.checkbox',
  [SectionComponent.Card]: 'components.card',
  [SectionComponent.Form]: 'components.form',
  [SectionComponent.StepperExpanded]: 'components.stepperExpanded',
  [SectionComponent.CommonStepperExpanded]: 'components.stepperExpanded',
  [SectionComponent.TableHeader]: 'components.tableHeader',
  [SectionComponent.ChatHeader]: 'components.chatHeader',
  [SectionComponent.ChatHeaderButton]: 'components.chatHeaderButton',
  [SectionComponent.ChatHistory]: 'components.chatHistory',
  [SectionComponent.ChatSuggestedPrompts]: 'components.chatSuggestedPrompts',
  [SectionComponent.ChatSuggestedPromptsSection]: 'components.chatSuggestedPromptsSection',
  [SectionComponent.ChatSuggestedPromptsItem]: 'components.chatSuggestedPromptsItem',
  [SectionComponent.ChatHistorySection]: 'components.chatHistorySection',
  [SectionComponent.ChatHistoryItem]: 'components.chatHistoryItem',
  [SectionComponent.ChatHistorySearch]: 'components.chatHistorySearch',
  [SectionComponent.ChatMessages]: 'components.chatMessages',
  [SectionComponent.ChatAgentMessage]: 'components.chatAgentMessage',
  [SectionComponent.ChatUserMessage]: 'components.chatUserMessage',
  [SectionComponent.ChatMessageActions]: 'components.chatMessageActions',
  [SectionComponent.ChatMessageFeedback]: 'components.chatMessageFeedback',
  [SectionComponent.ChatMessageFeedbackReason]: 'components.chatMessageFeedbackReason',
  [SectionComponent.ChatUserVoiceMessage]: 'components.chatUserVoiceMessage',
  [SectionComponent.ChatVoiceInput]: 'components.chatVoiceInput',
  [SectionComponent.ChatInput]: 'components.chatInput',
  [SectionComponent.ChatInputArea]: 'components.chatInputArea',
  [SectionComponent.ChatErrorMessage]: 'components.chatErrorMessage',
  [SectionComponent.ChatUserFileAttachments]: 'components.chatUserFileAttachments',
  [SectionComponent.ChatUserFileItem]: 'components.chatUserFileItem',
  [SectionComponent.ChatInputFileAttachments]: 'components.chatInputFileAttachments',
  [SectionComponent.ChatInputFileItem]: 'components.chatInputFileItem',
  [SectionComponent.BreadcrumbItem]: 'components.breadcrumbItem',
  [SectionComponent.MarkdownTable]: 'components.markdownTable',
  [SectionComponent.ModalHeader]: 'components.modalHeader',
}

/**
 * Returns the theme path for a chat section component, accounting for display mode.
 * Fill uses base paths (chat, chatHeader, etc.), FullScreen/Floating append suffix.
 */
export function getChatPathForDisplayMode(
  section: (typeof CHAT_DISPLAY_MODE_PATHS)[number],
  displayMode: DisplayMode
): string {
  const basePath = ThemePathsMap[section]
  const suffix = DISPLAY_MODE_SUFFIX[displayMode]
  if (!suffix) {
    return basePath
  }
  return basePath.replace(/\.([^.]+)$/, (_, lastPart) => `.${lastPart}${suffix}`)
}

/** Path for Floating Action Button (chatFab) – only for FullScreen and Floating on Web, always present on mobile */
export function getChatFabPathForDisplayMode(
  displayMode: DisplayMode,
  platform?: Platform
): string | null {
  if (platform && platform !== Platform.Web) {
    return 'components.chatFab'
  }
  switch (displayMode) {
    case DisplayMode.FULLSCREEN:
      return 'components.chatFabFullScreen'
    case DisplayMode.FLOATING:
      return 'components.chatFabFloating'
    default:
      return null
  }
}

/** Path for FAB notification dot (chatFabNotification) – mirrors getChatFabPathForDisplayMode availability */
export function getChatFabNotificationPathForDisplayMode(
  displayMode: DisplayMode,
  platform?: Platform
): string | null {
  if (platform && platform !== Platform.Web) {
    return 'components.chatFabNotification'
  }
  switch (displayMode) {
    case DisplayMode.FULLSCREEN:
      return 'components.chatFabNotificationFullScreen'
    case DisplayMode.FLOATING:
      return 'components.chatFabNotificationFloating'
    default:
      return null
  }
}

export const SECTIONS_WITHOUT_PLATFORM: Array<Section | SectionComponent | string> = [
  Section.Color,
  ThemePathsMap[Section.Color],
]

export const MOBILE_ONLY_SECTIONS: Array<Section | SectionComponent | string> = [
  Section.NavigationBar,
]

export const WEB_ONLY_SECTIONS: Array<Section | SectionComponent | string> = [
  Section.Table,
  Section.Breadcrumbs,
  SectionComponent.BreadcrumbItem,
  Section.Modal,
  SectionComponent.ModalHeader,
]

export const SECTIONS_WITH_DISPLAY_MODES: Array<Section | SectionComponent | string> = [
  Section.Chat,
]

/**
 * Converts a display-mode-specific path to the equivalent path for a different display mode.
 * E.g., 'components.chatHeaderFullScreen.icons' with (FULLSCREEN → FLOATING)
 * becomes 'components.chatHeaderFloating.icons'
 */
export function convertDisplayModePath(
  path: string,
  fromDisplayMode: DisplayMode,
  toDisplayMode: DisplayMode
): string {
  if (fromDisplayMode === toDisplayMode) {
    return path
  }

  const fromSuffix = DISPLAY_MODE_SUFFIX[fromDisplayMode]
  const toSuffix = DISPLAY_MODE_SUFFIX[toDisplayMode]

  const parts = path.split('.')
  if (parts.length >= 2) {
    const componentPart = parts[1]
    const baseName = fromSuffix
      ? componentPart.replace(new RegExp(`${fromSuffix}$`), '')
      : componentPart
    parts[1] = baseName + toSuffix
  }
  return parts.join('.')
}
